/**
 * Changing the equipment register from the screen: add, edit, transfer,
 * retire and reinstate an item, import a spreadsheet, and read the record of
 * every change. The rules — what may change, what needs a reason, what is
 * recorded — are in `domain/fleetEdit.ts`; this file is only the forms.
 *
 * Every save goes through `onSave(nextRegister)`, which the page wires to the
 * register's own store, so the change and its history entry are written
 * together and can never be saved apart.
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { useSession } from "../../app/SessionContext";
import { CONDITION_LABEL, CONDITION_ORDER, FLEET_KINDS, type FleetItem, type FleetRegister } from "../../domain/fleet";
import {
  ACTION_LABEL,
  addItem,
  applyImport,
  checkItem,
  EDIT_FIELDS,
  editItem,
  FIELD,
  FIELDS,
  flatten,
  OWNER_KIND_LABEL,
  planImport,
  reinstateItem,
  retireItem,
  shownValue,
  TEMPLATE_HEADINGS,
  templateCsv,
  transferItem,
  type Actor,
  type FieldError,
  type FieldKey,
  type FleetChange,
  type Flat,
  type ImportPlan,
} from "../../domain/fleetEdit";
import { Button, EmptyState, Field, Input, Select } from "../../design/components";
import { x as ex } from "../executive/shared";
import { t, tn } from "../../i18n";
import s from "./Equipment.module.css";
import { saveFile } from "../../lib/saveFile";

/*
 * `base` is the register the dialog opened on (build 5): only what the dialog
 * changed is sent, from the versions it read, so a colleague's change to the
 * same item since is refused rather than overwritten.
 */
type Save = (next: FleetRegister, base?: FleetRegister) => Promise<void>;

/** A saved time in the reader's own clock, as the header shows it — stored in UTC, never shown in it. */
export function localStamp(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The signed-in person, stamped on every change. */
export function useActor(): () => Actor {
  const session = useSession();
  return () => ({
    by: session.principal.name,
    at: new Date().toISOString(),
    newId: () => `eq-${crypto.randomUUID().slice(0, 8)}`,
  });
}

/* ── the dialog frame ─────────────────────────────────────────────────── */

function Dialog({
  title,
  onClose,
  onSubmit,
  children,
  wide,
  testId,
}: {
  title: string;
  onClose: () => void;
  onSubmit: (e: FormEvent) => void;
  children: ReactNode;
  wide?: boolean;
  testId?: string;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    /* no close() on cleanup — see CreateWorkOrderDialog for why */
  }, []);
  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={wide ? `${s.dialog} ${s.dialogWide}` : s.dialog}
      data-testid={testId}
    >
      <form onSubmit={onSubmit} className={s.form}>
        <h2 className={s.formWide}>{title}</h2>
        {children}
      </form>
    </dialog>
  );
}

function Foot({
  busy,
  failed,
  label,
  onClose,
  disabled,
}: {
  busy: boolean;
  failed: string | null;
  label: string;
  onClose: () => void;
  disabled?: boolean;
}) {
  return (
    <div className={s.formFoot}>
      {failed && (
        <span className={s.err} role="alert" style={{ marginInlineEnd: "auto" }}>
          {failed}
        </span>
      )}
      <Button onClick={onClose} disabled={busy}>
        {t("Cancel")}
      </Button>
      <Button type="submit" variant="primary" disabled={busy || disabled}>
        {busy ? t("Saving…") : label}
      </Button>
    </div>
  );
}

/** One field of the item form, drawn for its type. */
function FieldInput({
  k,
  value,
  onChange,
  error,
  lists,
}: {
  k: FieldKey;
  value: string;
  onChange: (v: string) => void;
  error?: FieldError;
  lists?: Partial<Record<FieldKey, string[]>>;
}) {
  const def = FIELD[k];
  const label = t(def.label);
  let input: ReactNode;
  if (def.type === "kind")
    input = (
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{t("Choose…")}</option>
        {FLEET_KINDS.map((o) => (
          <option key={o.key} value={o.key}>
            {t(o.label)}
          </option>
        ))}
      </Select>
    );
  else if (def.type === "condition")
    input = (
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        {CONDITION_ORDER.map((c) => (
          <option key={c} value={c}>
            {t(CONDITION_LABEL[c])}
          </option>
        ))}
      </Select>
    );
  else if (def.type === "ownerKind")
    input = (
      <Select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label}>
        <option value="">{t("Choose…")}</option>
        <option value="airport">{t(OWNER_KIND_LABEL.airport)}</option>
        <option value="stakeholder">{t(OWNER_KIND_LABEL.stakeholder)}</option>
      </Select>
    );
  else {
    const list = lists?.[k];
    input = (
      <>
        <Input
          type={def.type === "date" ? "date" : def.type === "number" ? "number" : "text"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
          list={list ? `fleet-list-${k}` : undefined}
          data-testid={`field-${k}`}
        />
        {list && (
          <datalist id={`fleet-list-${k}`}>
            {list.map((o) => (
              <option key={o} value={o} />
            ))}
          </datalist>
        )}
      </>
    );
  }
  return (
    <div>
      <Field label={def.label}>{input}</Field>
      {error && <div className={s.err}>{t(error.message)}</div>}
    </div>
  );
}

/** The owners and departments already on the register, offered as suggestions. */
function listsOf(reg: FleetRegister): Partial<Record<FieldKey, string[]>> {
  const uniq = (xs: (string | undefined)[]) => [...new Set(xs.filter((x): x is string => !!x))].sort();
  const depts = uniq(reg.items.flatMap((i) => [i.department, i.usedByDepartment]));
  return {
    owner: uniq(reg.items.map((i) => i.owner.name)),
    department: depts,
    usedByDepartment: depts,
    location: uniq(reg.items.map((i) => i.location)),
    driver: uniq(reg.items.map((i) => i.driver?.name)),
  };
}

const GROUPS: { key: string; title: string }[] = [
  { key: "identity", title: "What it is" },
  { key: "ownership", title: "Who owns it" },
  { key: "state", title: "Its condition and where it is" },
  { key: "driver", title: "Who drives it" },
  { key: "dates", title: "License, insurance, permit and inspection" },
];

/* ── add and edit ─────────────────────────────────────────────────────── */

export function ItemDialog({
  item,
  register,
  onSave,
  onClose,
  onAdded,
}: {
  /** absent to add a new item */
  item?: FleetItem;
  register: FleetRegister;
  onSave: Save;
  onClose: () => void;
  onAdded?: (tag: string) => void;
}) {
  const actor = useActor();
  const adding = !item;
  /* the register as it stood when the dialog opened: what this edit was made from */
  const [opened] = useState(register);
  const [flat, setFlat] = useState<Flat>(() => (item ? flatten(item) : { condition: "unknown", ownerKind: "airport" }));
  const [reason, setReason] = useState("");
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const lists = useMemo(() => listsOf(register), [register]);
  const errors = checkItem(flat, register, item?.id);
  const shown = adding ? FIELDS.map((f) => f.key) : EDIT_FIELDS;
  const errorFor = (k: FieldKey) => (tried ? errors.find((e) => e.field === k) : undefined);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTried(true);
    if (errors.length) return;
    setBusy(true);
    setFailed(null);
    try {
      const from = adding ? register : opened;
      const next = adding ? addItem(from, flat, actor(), reason) : editItem(from, item.id, flat, actor(), reason);
      if (next !== from) await onSave(next, from);
      if (adding) onAdded?.(flat.tag!.trim());
      onClose();
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={adding ? t("Add an item to the register") : t("Edit {tag}", { tag: item.tag })}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      wide
      testId="fleet-item-dialog"
    >
      {!adding && (
        <p className={`${s.formWide} ${ex.muted}`}>
          {t(
            "The tag cannot be changed: work orders and notes find the item by it. The owner and the department change by Transfer, which records why.",
          )}
        </p>
      )}
      {GROUPS.map((g) => {
        const keys = shown.filter((k) => FIELD[k].group === g.key);
        if (!keys.length) return null;
        return (
          <fieldset key={g.key} className={s.group}>
            <legend>{t(g.title)}</legend>
            {keys.map((k) => (
              <FieldInput
                key={k}
                k={k}
                value={flat[k] ?? ""}
                onChange={(v) => setFlat((f) => ({ ...f, [k]: v }))}
                error={errorFor(k)}
                lists={lists}
              />
            ))}
          </fieldset>
        );
      })}
      <div className={s.formWide}>
        <Field label={adding ? "Where it came from (optional)" : "Reason for the change (optional)"}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="fleet-reason" />
        </Field>
        <p className={ex.muted}>
          {t("A blank date is saved as not recorded, and the register shows it as such — never as met.")}
        </p>
      </div>
      {tried && errors.length > 0 && (
        <p className={`${s.formWide} ${s.err}`} role="alert">
          {tn(errors.length, "{n} thing to correct before saving.", "{n} things to correct before saving.")}
        </p>
      )}
      <Foot
        busy={busy}
        failed={failed}
        label={adding ? t("Add to the register") : t("Save the changes")}
        onClose={onClose}
      />
    </Dialog>
  );
}

/* ── transfer ─────────────────────────────────────────────────────────── */

export function TransferDialog({
  item,
  register,
  onSave,
  onClose,
}: {
  item: FleetItem;
  register: FleetRegister;
  onSave: Save;
  onClose: () => void;
}) {
  const actor = useActor();
  const lists = useMemo(() => listsOf(register), [register]);
  const [ownerKind, setOwnerKind] = useState(item.owner.kind);
  const [opened] = useState(register);
  const [owner, setOwner] = useState(item.owner.name);
  const [department, setDepartment] = useState(item.department ?? "");
  const [usedBy, setUsedBy] = useState(item.usedByDepartment ?? "");
  const [location, setLocation] = useState(item.location ?? "");
  const [effective, setEffective] = useState(new Date().toISOString().slice(0, 10));
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setFailed(t("A transfer needs a reason."));
    if (!owner.trim()) return setFailed(t("Name the owner."));
    setBusy(true);
    setFailed(null);
    try {
      const next = transferItem(
        opened,
        item.id,
        { ownerKind, owner, department, usedByDepartment: usedBy, location, effective },
        actor(),
        reason,
      );
      if (next === opened)
        return setFailed(t("Nothing has changed: the owner, department and location are as they were."));
      await onSave(next, opened);
      onClose();
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const text = (v: string, set: (v: string) => void, k: FieldKey) => (
    <>
      <Input
        value={v}
        onChange={(e) => set(e.target.value)}
        list={`transfer-${k}`}
        aria-label={t(FIELD[k].label)}
        data-testid={`transfer-${k}`}
      />
      <datalist id={`transfer-${k}`}>
        {(lists[k] ?? []).map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );

  return (
    <Dialog
      title={t("Transfer {tag}", { tag: item.tag })}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      testId="fleet-transfer-dialog"
    >
      <p className={`${s.formWide} ${ex.muted}`}>
        {t("Now: {owner} · {dept}", { owner: item.owner.name, dept: item.department ?? t("no department recorded") })}
      </p>
      <Field label="Owned by">
        <Select
          value={ownerKind}
          onChange={(e) => setOwnerKind(e.target.value as "airport" | "stakeholder")}
          aria-label={t("Owned by")}
        >
          <option value="airport">{t(OWNER_KIND_LABEL.airport)}</option>
          <option value="stakeholder">{t(OWNER_KIND_LABEL.stakeholder)}</option>
        </Select>
      </Field>
      <Field label="Owner">{text(owner, setOwner, "owner")}</Field>
      <Field label="Department">{text(department, setDepartment, "department")}</Field>
      <Field label="Used by department">{text(usedBy, setUsedBy, "usedByDepartment")}</Field>
      <Field label="Location">{text(location, setLocation, "location")}</Field>
      <Field label="Takes effect">
        <Input
          type="date"
          value={effective}
          onChange={(e) => setEffective(e.target.value)}
          aria-label={t("Takes effect")}
        />
      </Field>
      <div className={s.formWide}>
        <Field label="Reason for the transfer">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="transfer-reason" />
        </Field>
      </div>
      <Foot busy={busy} failed={failed} label={t("Record the transfer")} onClose={onClose} />
    </Dialog>
  );
}

/* ── retire and reinstate ─────────────────────────────────────────────── */

export function RetireDialog({
  item,
  register,
  onSave,
  onClose,
}: {
  item: FleetItem;
  register: FleetRegister;
  onSave: Save;
  onClose: () => void;
}) {
  const actor = useActor();
  const back = !!item.retired;
  const [opened] = useState(register);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!reason.trim()) return setFailed(t("A reason is needed."));
    setBusy(true);
    try {
      await onSave(
        back ? reinstateItem(opened, item.id, actor(), reason) : retireItem(opened, item.id, actor(), reason),
        opened,
      );
      onClose();
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={back ? t("Reinstate {tag}", { tag: item.tag }) : t("Retire {tag}", { tag: item.tag })}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      testId="fleet-retire-dialog"
    >
      <p className={`${s.formWide} ${ex.muted}`}>
        {back
          ? t("It returns to the working register with everything it had.")
          : t(
              "It leaves the working register but is not deleted: its history, its notes and its tag are kept, and the tag is never given to another item.",
            )}
      </p>
      <div className={s.formWide}>
        <Field label={back ? "Why it is coming back" : "Why it is being retired"}>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="retire-reason" />
        </Field>
      </div>
      <Foot busy={busy} failed={failed} label={back ? t("Reinstate") : t("Retire")} onClose={onClose} />
    </Dialog>
  );
}

/* ── the buttons on one item ──────────────────────────────────────────── */

export function ItemActions({ item, register, onSave }: { item: FleetItem; register: FleetRegister; onSave: Save }) {
  const [open, setOpen] = useState<"edit" | "transfer" | "retire" | null>(null);
  const close = () => setOpen(null);
  return (
    <>
      {!item.retired && (
        <>
          <Button onClick={() => setOpen("edit")} data-testid={`fleet-edit-${item.tag}`}>
            {t("Edit")}
          </Button>
          <Button onClick={() => setOpen("transfer")} data-testid={`fleet-transfer-${item.tag}`}>
            {t("Transfer")}
          </Button>
        </>
      )}
      <Button onClick={() => setOpen("retire")} data-testid={`fleet-retire-${item.tag}`}>
        {item.retired ? t("Reinstate") : t("Retire")}
      </Button>
      {open === "edit" && <ItemDialog item={item} register={register} onSave={onSave} onClose={close} />}
      {open === "transfer" && <TransferDialog item={item} register={register} onSave={onSave} onClose={close} />}
      {open === "retire" && <RetireDialog item={item} register={register} onSave={onSave} onClose={close} />}
    </>
  );
}

/* ── import ───────────────────────────────────────────────────────────── */

const STATUS_LABEL = { new: "new", update: "update", same: "no change", error: "cannot import" } as const;

export function ImportDialog({
  register,
  onSave,
  onClose,
}: {
  register: FleetRegister;
  onSave: Save;
  onClose: () => void;
}) {
  const actor = useActor();
  const [text, setText] = useState("");
  const [source, setSource] = useState("");
  const [plan, setPlan] = useState<ImportPlan | null>(null);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [done, setDone] = useState<number | null>(null);

  const read = async (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (/\.xlsx?$/i.test(f.name)) {
      setFailed(
        t(
          "An Excel workbook cannot be read directly. In Excel choose Save As › CSV, or copy the rows and paste them below.",
        ),
      );
      return;
    }
    setFailed(null);
    const body = await f.text();
    setText(body);
    setSource(f.name);
    setPlan(planImport(register, body));
  };

  const check = () => {
    setFailed(null);
    setPlan(planImport(register, text));
    if (!source) setSource(t("Rows pasted on {date}", { date: new Date().toISOString().slice(0, 10) }));
  };

  const ready = plan?.rows.filter((r) => r.status === "new" || r.status === "update") ?? [];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!plan || ready.length === 0) return;
    setBusy(true);
    try {
      await onSave(applyImport(register, plan, actor(), source.trim() || t("Imported rows")));
      setDone(ready.length);
    } catch (err) {
      setFailed(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const download = () => {
    void saveFile("equipment-register-template.csv", templateCsv(), "text/csv;charset=utf-8");
  };

  if (done !== null)
    return (
      <Dialog
        title={t("Import the register")}
        onClose={onClose}
        onSubmit={(e) => (e.preventDefault(), onClose())}
        testId="fleet-import-dialog"
      >
        <p className={`${s.formWide} ${ex.saved}`} data-testid="import-done">
          {tn(
            done,
            "{n} item imported. Each change is in the change log.",
            "{n} items imported. Each change is in the change log.",
          )}
        </p>
        <div className={s.formFoot}>
          <Button type="submit" variant="primary">
            {t("Close")}
          </Button>
        </div>
      </Dialog>
    );

  return (
    <Dialog
      title={t("Import the register")}
      onClose={onClose}
      onSubmit={(e) => void submit(e)}
      wide
      testId="fleet-import-dialog"
    >
      <div className={s.formWide}>
        <p className={ex.muted}>
          {t(
            "Choose a CSV file, or copy the rows from Excel and paste them here. The first row names the columns. A row whose tag is already on the register updates that item; a new tag adds one. A blank cell leaves what is recorded as it is — an import never erases a date. Dates are YYYY-MM-DD or day/month/year.",
          )}
        </p>
        <div className={s.template}>
          <span className={ex.muted}>{t("Columns:")}</span> <code>{TEMPLATE_HEADINGS.join(", ")}</code>
        </div>
        <div className={ex.row}>
          <Button onClick={download}>{t("Download the template")}</Button>
          <input
            type="file"
            accept=".csv,.tsv,.txt,.xlsx,.xls"
            onChange={(e) => void read(e)}
            aria-label={t("Choose a file")}
            data-testid="import-file"
          />
        </div>
      </div>
      <div className={s.formWide}>
        <Field label="Or paste the rows">
          <textarea
            className={s.paste}
            rows={6}
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setPlan(null);
            }}
            data-testid="import-text"
          />
        </Field>
        <Button onClick={check} disabled={!text.trim()} data-testid="import-check">
          {t("Check the rows")}
        </Button>
      </div>

      {plan?.problem && (
        <p className={`${s.formWide} ${s.err}`} role="alert">
          {t(plan.problem)}
        </p>
      )}
      {plan && !plan.problem && (
        <div className={s.formWide} data-testid="import-plan">
          <p>
            {t("{new} new, {update} to update, {same} unchanged, {error} that cannot be imported.", {
              new: plan.rows.filter((r) => r.status === "new").length,
              update: plan.rows.filter((r) => r.status === "update").length,
              same: plan.rows.filter((r) => r.status === "same").length,
              error: plan.rows.filter((r) => r.status === "error").length,
            })}
          </p>
          {plan.ignored.length > 0 && (
            <p className={ex.muted}>
              {t("Columns not recognized, and ignored: {cols}", { cols: plan.ignored.join(", ") })}
            </p>
          )}
          <div className={ex.tableWrap} style={{ maxHeight: 280, overflow: "auto" }}>
            <table className={ex.table}>
              <tbody>
                {plan.rows.map((r) => (
                  <tr key={r.line}>
                    <td>{t("line {n}", { n: r.line })}</td>
                    <td>
                      <b>{r.tag || "—"}</b>
                    </td>
                    <td>
                      <span className={r.status === "error" ? ex.tagRed : r.status === "same" ? ex.tag : ex.tagGreen}>
                        {t(STATUS_LABEL[r.status])}
                      </span>
                    </td>
                    <td>
                      {r.errors.map((e, i) => (
                        <div key={i} className={s.err}>
                          {e.field ? `${t(FIELD[e.field].label)}: ` : ""}
                          {t(e.message)}
                          {e.value ? ` “${e.value}”` : ""}
                        </div>
                      ))}
                      {r.status === "update" && <FieldList fields={r.fields} />}
                      {r.status === "new" && (
                        <span className={ex.muted}>{tn(r.fields.length, "{n} field", "{n} fields")}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Field label="Where these rows came from">
            <Input value={source} onChange={(e) => setSource(e.target.value)} data-testid="import-source" />
          </Field>
        </div>
      )}
      <Foot
        busy={busy}
        failed={failed}
        label={tn(ready.length, "Import {n} item", "Import {n} items")}
        onClose={onClose}
        disabled={ready.length === 0}
      />
    </Dialog>
  );
}

/* ── the record of changes ────────────────────────────────────────────── */

function Value({ field, v }: { field: FieldKey; v?: string }) {
  const shown = shownValue(field, v);
  const type = FIELD[field].type;
  if (!shown) return <span className={ex.muted}>{t("not recorded")}</span>;
  return <b>{type === "kind" || type === "condition" || type === "ownerKind" ? t(shown) : shown}</b>;
}

function FieldList({ fields }: { fields: FleetChange["fields"] }) {
  return (
    <ul className={s.fieldList}>
      {fields.map((f) => (
        <li key={f.field}>
          {t(FIELD[f.field].label)}: <Value field={f.field} v={f.from} /> → <Value field={f.field} v={f.to} />
        </li>
      ))}
    </ul>
  );
}

/**
 * The change log, newest first. `showTag` for the register-wide log, where
 * each entry links to the item it is about.
 */
export function ChangeLog({
  changes,
  showTag,
  limit,
}: {
  changes: readonly FleetChange[];
  showTag?: boolean;
  limit?: number;
}) {
  const [all, setAll] = useState(false);
  const newest = [...changes].sort((a, b) => b.at.localeCompare(a.at));
  const shown = limit && !all ? newest.slice(0, limit) : newest;
  if (newest.length === 0)
    return <EmptyState>{t("No change has been made since the register was first entered.")}</EmptyState>;
  return (
    <>
      <div className={ex.tableWrap}>
        <table className={ex.table} data-testid="fleet-changes">
          <tbody>
            {shown.map((c) => (
              <tr key={c.id}>
                <td className={s.when}>
                  {localStamp(c.at)}
                  <div className={ex.muted}>{c.by}</div>
                </td>
                <td>
                  <span
                    className={c.action === "retired" ? ex.tagRed : c.action === "transferred" ? ex.tag : ex.tagGreen}
                  >
                    {t(ACTION_LABEL[c.action])}
                  </span>
                  {showTag && (
                    <div>
                      <Link to={`/equipment/${encodeURIComponent(c.tag)}`}>{c.tag}</Link>
                    </div>
                  )}
                </td>
                <td>
                  {c.action === "added" || (c.action === "imported" && c.fields.every((f) => !f.from)) ? (
                    <span className={ex.muted}>{tn(c.fields.length, "{n} field recorded", "{n} fields recorded")}</span>
                  ) : (
                    <FieldList fields={c.fields} />
                  )}
                  {c.effective && <div className={ex.muted}>{t("takes effect {date}", { date: c.effective })}</div>}
                  {c.reason && <div className={s.reason}>{t("Reason: {why}", { why: c.reason })}</div>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {limit && newest.length > limit && (
        <Button onClick={() => setAll((v) => !v)}>
          {all ? t("Show the latest only") : tn(newest.length, "Show all {n} changes", "Show all {n} changes")}
        </Button>
      )}
    </>
  );
}
